export const RECIPES = {
    // ── Pre-P6 baselines ──────────────────────────────────────────────────────
    'extract-iron-ore': {
        id: 'extract-iron-ore',
        name: 'Extract Iron Ore',
        inputs: [],
        outputs: [{ resource: 'iron-ore', qty: 1 }],
        cycleTicks: 25, // 5 s at 5 Hz — see lore §7 T1 baseline
    },
    'extract-coal': {
        id: 'extract-coal',
        name: 'Extract Coal',
        inputs: [],
        outputs: [{ resource: 'coal', qty: 1 }],
        cycleTicks: 25,
    },
    'smelt-iron-ingot': {
        id: 'smelt-iron-ingot',
        name: 'Smelt Iron Ingot',
        inputs: [{ resource: 'iron-ore', qty: 2 }],
        outputs: [{ resource: 'iron-ingot', qty: 1 }],
        cycleTicks: 50, // 10 s at 5 Hz — see lore §7 T2 baseline
    },
    'refine-fuel': {
        id: 'refine-fuel',
        name: 'Refine Fuel',
        inputs: [{ resource: 'coal', qty: 2 }],
        outputs: [{ resource: 'refined-fuel', qty: 1 }],
        cycleTicks: 40,
    },
    'burn-refined-fuel': {
        id: 'burn-refined-fuel',
        name: 'Burn Refined Fuel',
        inputs: [{ resource: 'refined-fuel', qty: 1 }],
        outputs: [],
        cycleTicks: 50,
    },
    // ── P6 extraction (T1 Materials track) ────────────────────────────────────
    'extract-copper-ore': {
        id: 'extract-copper-ore',
        name: 'Extract Copper Ore',
        inputs: [],
        outputs: [{ resource: 'copper-ore', qty: 1 }],
        cycleTicks: 25,
    },
    'extract-bauxite': {
        id: 'extract-bauxite',
        name: 'Extract Bauxite',
        inputs: [],
        outputs: [{ resource: 'bauxite', qty: 1 }],
        cycleTicks: 30,
    },
    // ── P6 heavy-drill variants (faster, higher draw, same outputs) ──────────
    // Same goods, bigger numbers — the heavy drill earns its kW by finishing
    // ~40% faster than the standard drill on each deposit.
    'heavy-extract-iron-ore': {
        id: 'heavy-extract-iron-ore',
        name: 'Heavy Extract Iron Ore',
        inputs: [],
        outputs: [{ resource: 'iron-ore', qty: 2 }],
        cycleTicks: 25,
    },
    'heavy-extract-coal': {
        id: 'heavy-extract-coal',
        name: 'Heavy Extract Coal',
        inputs: [],
        outputs: [{ resource: 'coal', qty: 2 }],
        cycleTicks: 25,
    },
    'heavy-extract-copper-ore': {
        id: 'heavy-extract-copper-ore',
        name: 'Heavy Extract Copper Ore',
        inputs: [],
        outputs: [{ resource: 'copper-ore', qty: 2 }],
        cycleTicks: 25,
    },
    'heavy-extract-bauxite': {
        id: 'heavy-extract-bauxite',
        name: 'Heavy Extract Bauxite',
        inputs: [],
        outputs: [{ resource: 'bauxite', qty: 2 }],
        cycleTicks: 30,
    },
    // ── P6 atmospheric collection (no deposit, just air + power) ─────────────
    'collect-water': {
        id: 'collect-water',
        name: 'Collect Water',
        inputs: [],
        outputs: [{ resource: 'water', qty: 1 }],
        cycleTicks: 60, // slow trickle so it doesn't trivialise the chain
    },
    'collect-oxygen': {
        id: 'collect-oxygen',
        name: 'Collect Oxygen',
        inputs: [],
        outputs: [{ resource: 'oxygen', qty: 1 }],
        cycleTicks: 80,
    },
    // ── P6 refining ─────────────────────────────────────────────────────────
    'smelt-copper-plate': {
        id: 'smelt-copper-plate',
        name: 'Smelt Copper Plate',
        inputs: [{ resource: 'copper-ore', qty: 2 }],
        outputs: [{ resource: 'copper-plate', qty: 1 }],
        cycleTicks: 50,
    },
    'kiln-alumina': {
        id: 'kiln-alumina',
        name: 'Calcine Alumina',
        inputs: [{ resource: 'bauxite', qty: 2 }],
        outputs: [{ resource: 'alumina', qty: 1 }],
        cycleTicks: 60,
    },
    'electrolyse-water': {
        // The keystone Energy-track recipe: 2 water → 1 hydrogen + 1 oxygen.
        // Only the Hydrolysis Cell (or, electrically equivalent, the Electrolyzer)
        // can run it. Multiple outputs make the inventory plumbing a real test.
        id: 'electrolyse-water',
        name: 'Electrolyse Water',
        inputs: [{ resource: 'water', qty: 2 }],
        outputs: [
            { resource: 'hydrogen', qty: 1 },
            { resource: 'oxygen', qty: 1 },
        ],
        cycleTicks: 70,
    },
    'compress-hydrogen-cryo': {
        // Cryo Separator condenses gaseous hydrogen into a cryogenic fluid.
        // Output is `cryogenic: true`, which forces the cryo drone class in
        // any rule that picks it up.
        id: 'compress-hydrogen-cryo',
        name: 'Compress Hydrogen → LH₂',
        inputs: [{ resource: 'hydrogen', qty: 4 }],
        outputs: [{ resource: 'liquid-hydrogen', qty: 1 }],
        cycleTicks: 80,
    },
    // ── P6 fabrication ──────────────────────────────────────────────────────
    'extrude-copper-wire': {
        id: 'extrude-copper-wire',
        name: 'Extrude Copper Wire',
        inputs: [{ resource: 'copper-plate', qty: 1 }],
        outputs: [{ resource: 'copper-wire', qty: 2 }],
        cycleTicks: 40,
    },
    'assemble-basic-circuit': {
        // First two-input recipe in the catalog. Iron + copper-wire → circuit:
        // showcases the Assembler as the gateway to multi-source production.
        id: 'assemble-basic-circuit',
        name: 'Assemble Basic Circuit',
        inputs: [
            { resource: 'iron-ingot', qty: 1 },
            { resource: 'copper-wire', qty: 2 },
        ],
        outputs: [{ resource: 'basic-circuit', qty: 1 }],
        cycleTicks: 60,
    },
    // ── P6A biotech chain ─────────────────────────────────────────────────
    'extract-algae-slurry': {
        // Algae Reactor's only recipe. Like the drill's `extract-iron-ore` but
        // the gating tile is `water-source`, not a deposit. No inputs — algae
        // grows itself given water + sunlight.
        id: 'extract-algae-slurry',
        name: 'Extract Algae Slurry',
        inputs: [],
        outputs: [{ resource: 'algae-slurry', qty: 1 }],
        cycleTicks: 35,
    },
    'culture-microbes': {
        // Slurry → live microbial culture. Single input, fluid in, fluid out.
        id: 'culture-microbes',
        name: 'Culture Microbes',
        inputs: [{ resource: 'algae-slurry', qty: 2 }],
        outputs: [{ resource: 'microbial-culture', qty: 1 }],
        cycleTicks: 55,
    },
    'press-biomass': {
        // Solid output from a fluid input — the "drying" step. Lets freighters
        // keep doing useful work in a chain that's otherwise tanker-only.
        id: 'press-biomass',
        name: 'Press Bio-Mass',
        inputs: [{ resource: 'algae-slurry', qty: 3 }],
        outputs: [{ resource: 'bio-mass', qty: 1 }],
        cycleTicks: 50,
    },
    'ferment-biofuel': {
        // Two-input recipe — culture (fluid) + biomass (solid) → fuel (fluid).
        // Mixed phases force the player to think about which class to commission
        // for each leg of the chain.
        id: 'ferment-biofuel',
        name: 'Ferment Biofuel',
        inputs: [
            { resource: 'microbial-culture', qty: 1 },
            { resource: 'bio-mass', qty: 2 },
        ],
        outputs: [{ resource: 'fermented-fuel', qty: 1 }],
        cycleTicks: 70,
    },
    'brew-pharma-precursor': {
        // Culture Lab — high-power, T2 fluid output that's flagged cryogenic, so
        // moving it requires a Cryo class drone (or watching the cargo flash off).
        id: 'brew-pharma-precursor',
        name: 'Brew Pharma Precursor',
        inputs: [
            { resource: 'microbial-culture', qty: 2 },
            { resource: 'alumina', qty: 1 },
        ],
        outputs: [{ resource: 'pharma-precursor', qty: 1 }],
        cycleTicks: 90,
    },
    // ── P7B T3 Materials ──────────────────────────────────────────────────
    'forge-superalloy': {
        // Plasma Refinery — high-temp forge. Pulls iron + alumina + oxygen
        // (oxidising flux) into a high-tier metal stock. Long cycle, big payoff.
        id: 'forge-superalloy',
        name: 'Forge Superalloy',
        inputs: [
            { resource: 'iron-ingot', qty: 2 },
            { resource: 'alumina', qty: 1 },
            { resource: 'oxygen', qty: 1 },
        ],
        outputs: [{ resource: 'superalloy', qty: 1 }],
        cycleTicks: 100,
    },
    'fire-ceramic-composite': {
        id: 'fire-ceramic-composite',
        name: 'Fire Ceramic Composite',
        inputs: [
            { resource: 'alumina', qty: 2 },
            { resource: 'bauxite', qty: 1 },
        ],
        outputs: [{ resource: 'ceramic-composite', qty: 1 }],
        cycleTicks: 90,
    },
    'machine-precision-bearing': {
        // Machining Center — turns superalloy stock + copper into precision
        // bearings. The classic "valuable end-product" recipe.
        id: 'machine-precision-bearing',
        name: 'Machine Precision Bearing',
        inputs: [
            { resource: 'superalloy', qty: 1 },
            { resource: 'copper-plate', qty: 1 },
        ],
        outputs: [{ resource: 'precision-bearing', qty: 1 }],
        cycleTicks: 110,
    },
    'mill-semiconductor-wafer': {
        // Wafer Mill — copper + alumina substrate produces semiconductor wafers.
        // Late-game material for the lithium-ion module chain.
        id: 'mill-semiconductor-wafer',
        name: 'Mill Semiconductor Wafer',
        inputs: [
            { resource: 'copper-plate', qty: 2 },
            { resource: 'alumina', qty: 1 },
        ],
        outputs: [{ resource: 'semiconductor-wafer', qty: 1 }],
        cycleTicks: 95,
    },
    // ── P7B T3 Energy ─────────────────────────────────────────────────────
    'forge-plasma-cell': {
        // Plasma Refinery — alt recipe. Takes superalloy + hydrogen and forges
        // a plasma containment cell. Demonstrates the plasma refinery's
        // dual-purpose chassis (also forges superalloy via forge-superalloy).
        id: 'forge-plasma-cell',
        name: 'Forge Plasma Cell',
        inputs: [
            { resource: 'superalloy', qty: 1 },
            { resource: 'hydrogen', qty: 2 },
        ],
        outputs: [{ resource: 'plasma-cell', qty: 1 }],
        cycleTicks: 100,
    },
    'assemble-lithium-ion': {
        // Nuclear Cell Plant — assembles wafer + copper-wire + bio-fuel
        // electrolyte into a lithium-ion module. Cross-track recipe (uses a
        // biotech good) so endgame players actually need the biotech chain
        // running.
        id: 'assemble-lithium-ion',
        name: 'Assemble Lithium-Ion Module',
        inputs: [
            { resource: 'semiconductor-wafer', qty: 1 },
            { resource: 'copper-wire', qty: 2 },
            { resource: 'fermented-fuel', qty: 1 },
        ],
        outputs: [{ resource: 'lithium-ion-module', qty: 1 }],
        cycleTicks: 110,
    },
    'synthesize-fusion-precursor': {
        // Nuclear Cell Plant — alt recipe. Plasma cells + LH₂ → cryogenic
        // fusion precursor. Highest-tier energy good in P7B; cryogenic so
        // moving it requires a Cryo drone.
        id: 'synthesize-fusion-precursor',
        name: 'Synthesize Fusion Precursor',
        inputs: [
            { resource: 'plasma-cell', qty: 2 },
            { resource: 'liquid-hydrogen', qty: 1 },
        ],
        outputs: [{ resource: 'fusion-precursor', qty: 1 }],
        cycleTicks: 130,
    },
    // ── P7B T3 Biotech ────────────────────────────────────────────────────
    'culture-antibiotic': {
        // Bio-Reactor Array — pharma precursor + culture → antibiotic.
        id: 'culture-antibiotic',
        name: 'Culture Antibiotic',
        inputs: [
            { resource: 'microbial-culture', qty: 2 },
            { resource: 'pharma-precursor', qty: 1 },
        ],
        outputs: [{ resource: 'antibiotic', qty: 1 }],
        cycleTicks: 105,
    },
    'synth-protein-batch': {
        id: 'synth-protein-batch',
        name: 'Synthesize Protein',
        inputs: [
            { resource: 'algae-slurry', qty: 3 },
            { resource: 'microbial-culture', qty: 1 },
        ],
        outputs: [{ resource: 'synth-protein', qty: 1 }],
        cycleTicks: 90,
    },
    'grow-organ-scaffold': {
        // Top-tier biotech good — uses two T3 inputs.
        id: 'grow-organ-scaffold',
        name: 'Grow Organ Scaffold',
        inputs: [
            { resource: 'synth-protein', qty: 2 },
            { resource: 'antibiotic', qty: 1 },
        ],
        outputs: [{ resource: 'organ-scaffold', qty: 1 }],
        cycleTicks: 140,
    },
    // ── P8+ T4 — Materials / Matrix substrates ────────────────────────────
    'weave-mesh-composite': {
        // Matrix Core's keystone recipe. Composes two T3 metal goods + a
        // ceramic binder into the Matrix-tier substrate. Long cycle, high
        // value — the gateway to every other T4 chain.
        id: 'weave-mesh-composite',
        name: 'Weave Mesh Composite',
        inputs: [
            { resource: 'superalloy', qty: 2 },
            { resource: 'precision-bearing', qty: 1 },
            { resource: 'ceramic-composite', qty: 2 },
        ],
        outputs: [{ resource: 'mesh-composite', qty: 1 }],
        cycleTicks: 160,
    },
    'forge-exotic-alloy': {
        // Crystal Lathe alt — an exotic compound that feeds resonant coils.
        id: 'forge-exotic-alloy',
        name: 'Forge Exotic Alloy',
        inputs: [
            { resource: 'superalloy', qty: 1 },
            { resource: 'plasma-cell', qty: 1 },
            { resource: 'oxygen', qty: 2 },
        ],
        outputs: [{ resource: 'exotic-alloy', qty: 1 }],
        cycleTicks: 140,
    },
    'grow-quantum-crystal': {
        // Crystal Lathe primary recipe. Plasma + alumina substrate + hydrogen
        // bath grows the crystals that feed circuits and disks.
        id: 'grow-quantum-crystal',
        name: 'Grow Quantum Crystal',
        inputs: [
            { resource: 'plasma-cell', qty: 1 },
            { resource: 'alumina', qty: 1 },
            { resource: 'hydrogen', qty: 3 },
        ],
        outputs: [{ resource: 'quantum-crystal', qty: 1 }],
        cycleTicks: 150,
    },
    'fab-matrix-disk': {
        // Disk Fab — quantum-crystal + ceramic-composite → matrix-disk. The
        // raw infrastructure of the future networked-storage system.
        id: 'fab-matrix-disk',
        name: 'Fab Matrix Disk',
        inputs: [
            { resource: 'quantum-crystal', qty: 1 },
            { resource: 'ceramic-composite', qty: 1 },
        ],
        outputs: [{ resource: 'matrix-disk', qty: 1 }],
        cycleTicks: 130,
    },
    // ── T4 Energy ───────────────────────────────────────────────────────────
    'spin-photonic-cell': {
        // Photonic Loom — turns Li-Ion + mesh into photonic-cell, a higher-
        // tier batt with no mass-conservation hand-waving (lore says it works).
        id: 'spin-photonic-cell',
        name: 'Spin Photonic Cell',
        inputs: [
            { resource: 'lithium-ion-module', qty: 1 },
            { resource: 'mesh-composite', qty: 1 },
        ],
        outputs: [{ resource: 'photonic-cell', qty: 1 }],
        cycleTicks: 130,
    },
    'lay-plasma-conduit': {
        // Photonic Loom alt. Two plasma cells + copper wire → plasma conduit.
        id: 'lay-plasma-conduit',
        name: 'Lay Plasma Conduit',
        inputs: [
            { resource: 'plasma-cell', qty: 2 },
            { resource: 'copper-wire', qty: 1 },
        ],
        outputs: [{ resource: 'plasma-conduit', qty: 1 }],
        cycleTicks: 120,
    },
    'wind-resonant-coil': {
        // Coil Forge primary — plasma-conduit + exotic alloy → resonant coil.
        // Sets up T5 fusion chains.
        id: 'wind-resonant-coil',
        name: 'Wind Resonant Coil',
        inputs: [
            { resource: 'plasma-conduit', qty: 1 },
            { resource: 'exotic-alloy', qty: 1 },
        ],
        outputs: [{ resource: 'resonant-coil', qty: 1 }],
        cycleTicks: 150,
    },
    'imprint-quantum-circuit': {
        // Coil Forge alt — wafer + crystal + wire → top-tier quantum circuit.
        id: 'imprint-quantum-circuit',
        name: 'Imprint Quantum Circuit',
        inputs: [
            { resource: 'semiconductor-wafer', qty: 1 },
            { resource: 'quantum-crystal', qty: 1 },
            { resource: 'copper-wire', qty: 2 },
        ],
        outputs: [{ resource: 'quantum-circuit', qty: 1 }],
        cycleTicks: 140,
    },
    // ── T4 Biotech ──────────────────────────────────────────────────────────
    'vat-neural-substrate': {
        // Neural Vat primary — antibiotic + synth-protein → neural substrate.
        id: 'vat-neural-substrate',
        name: 'Vat Neural Substrate',
        inputs: [
            { resource: 'antibiotic', qty: 1 },
            { resource: 'synth-protein', qty: 1 },
        ],
        outputs: [{ resource: 'neural-substrate', qty: 1 }],
        cycleTicks: 130,
    },
    'grow-bio-neural-cortex': {
        // Neural Vat alt — top biotech good. Cross-track: organ-scaffold (T3
        // bio) + neural-substrate (T4 bio) + quantum-circuit (T4 mat) → cortex.
        // Forces all three tracks to be running for endgame biotech.
        id: 'grow-bio-neural-cortex',
        name: 'Grow Bio-Neural Cortex',
        inputs: [
            { resource: 'organ-scaffold', qty: 1 },
            { resource: 'neural-substrate', qty: 2 },
            { resource: 'quantum-circuit', qty: 1 },
        ],
        outputs: [{ resource: 'bio-neural-cortex', qty: 1 }],
        cycleTicks: 200,
    },
    // ── P8+ T5 Resonant Matrix recipes ──────────────────────────────────────
    // Every T5 recipe consumes T4 goods, so reaching T5 throughput requires
    // the entire T1→T4 supply chain to be live. Cycles are deliberately long
    // (200–280 ticks = 40–56 s at 5 Hz) — T5 production is the endgame
    // pacing element, not a fast loop.
    'forge-singularity-dust': {
        // Singularity Forge — the keystone T5 recipe. Cross-track inputs (T4
        // biotech + T4 energy + T4 materials) forge the exotic-matter dust
        // that every other T5 recipe needs.
        id: 'forge-singularity-dust',
        name: 'Forge Singularity Dust',
        inputs: [
            { resource: 'bio-neural-cortex', qty: 1 },
            { resource: 'plasma-conduit', qty: 2 },
            { resource: 'resonant-coil', qty: 1 },
        ],
        outputs: [{ resource: 'singularity-dust', qty: 1 }],
        cycleTicks: 240,
    },
    'weave-resonant-lattice': {
        // Lattice Weaver primary — singularity-dust + matrix infrastructure
        // (mesh-composite + matrix-disk) → resonant lattice. Requires the
        // Cohesion Matrix substrate goods to be flowing.
        id: 'weave-resonant-lattice',
        name: 'Weave Resonant Lattice',
        inputs: [
            { resource: 'singularity-dust', qty: 1 },
            { resource: 'mesh-composite', qty: 3 },
            { resource: 'matrix-disk', qty: 2 },
        ],
        outputs: [{ resource: 'resonant-lattice', qty: 1 }],
        cycleTicks: 220,
    },
    'cast-void-glass': {
        // Void Fabricator primary — exotic transparent substrate. Quantum
        // crystal core + mesh-composite carapace, oxidized in pure O₂.
        id: 'cast-void-glass',
        name: 'Cast Void Glass',
        inputs: [
            { resource: 'quantum-crystal', qty: 2 },
            { resource: 'mesh-composite', qty: 1 },
            { resource: 'oxygen', qty: 4 },
        ],
        outputs: [{ resource: 'void-glass', qty: 1 }],
        cycleTicks: 200,
    },
    'power-aeon-cell': {
        // Aeon Reactor primary — endgame battery. Combines photonic + resonant
        // tracks with a singularity-dust catalyst.
        id: 'power-aeon-cell',
        name: 'Power Aeon Cell',
        inputs: [
            { resource: 'photonic-cell', qty: 2 },
            { resource: 'resonant-coil', qty: 1 },
            { resource: 'singularity-dust', qty: 1 },
        ],
        outputs: [{ resource: 'aeon-cell', qty: 1 }],
        cycleTicks: 260,
    },
    'lay-zero-point-conduit': {
        // Aeon Reactor alt — zero-point conduit for the future autofab grid.
        // Pairs plasma-conduit with resonant-lattice, demanding both T4 and
        // T5 throughput.
        id: 'lay-zero-point-conduit',
        name: 'Lay Zero-Point Conduit',
        inputs: [
            { resource: 'plasma-conduit', qty: 2 },
            { resource: 'resonant-lattice', qty: 1 },
        ],
        outputs: [{ resource: 'zero-point-conduit', qty: 1 }],
        cycleTicks: 220,
    },
    'transcend-cortex': {
        // Transcendent Vat primary — sublimates a bio-neural cortex into the
        // top biotech good. Cross-track: T4 biotech + T4 substrate + T5 dust.
        id: 'transcend-cortex',
        name: 'Transcend Cortex',
        inputs: [
            { resource: 'bio-neural-cortex', qty: 2 },
            { resource: 'neural-substrate', qty: 3 },
            { resource: 'singularity-dust', qty: 1 },
        ],
        outputs: [{ resource: 'transcendent-cortex', qty: 1 }],
        cycleTicks: 280,
    },
    'archive-genes': {
        // Transcendent Vat alt — gene archive recipe. Cross-track: T3 bio +
        // T4 bio + T4 matrix infrastructure. Stores a sequenced genome onto
        // a matrix-disk substrate.
        id: 'archive-genes',
        name: 'Archive Genes',
        inputs: [
            { resource: 'synth-protein', qty: 3 },
            { resource: 'neural-substrate', qty: 2 },
            { resource: 'matrix-disk', qty: 1 },
        ],
        outputs: [{ resource: 'gene-archive', qty: 1 }],
        cycleTicks: 240,
    },
    // ── P14 generator burn recipes ──────────────────────────────────────────
    // Generators run a recipe with empty outputs — the cycle consumes fuel
    // and the spec's `generationKw` is the actual "output" of the machine
    // (kilowatts onto the grid; see `power.ts`). Cycle length controls fuel
    // consumption rate; faster cycle = more fuel burned per second.
    'burn-fermented-fuel': {
        // Bio-Burner — biotech alt to combustion-cell. Cross-pollinates the
        // Energy track with the biotech chain (P6A's fermented-fuel feeds it).
        id: 'burn-fermented-fuel',
        name: 'Burn Fermented Fuel',
        inputs: [{ resource: 'fermented-fuel', qty: 1 }],
        outputs: [],
        cycleTicks: 40, // 8 s at 5 Hz — slightly faster than combustion-cell
    },
    'burn-fusion-precursor': {
        // Fusion Reactor — T3 Energy, the lore's mid-game "everything works
        // again" power generator. Consumes the cryogenic T3 Energy good.
        id: 'burn-fusion-precursor',
        name: 'Burn Fusion Precursor',
        inputs: [{ resource: 'fusion-precursor', qty: 1 }],
        outputs: [],
        cycleTicks: 60, // 12 s at 5 Hz
    },
    'burn-plasma-cell': {
        // Heavy Reactor — T3 Energy alt (lore's "Thorium Reactor" slot).
        // Long cycle, big output: the demand-smoothing problem the lore
        // promised. One plasma-cell burns for a full minute.
        id: 'burn-plasma-cell',
        name: 'Burn Plasma Cell',
        inputs: [{ resource: 'plasma-cell', qty: 1 }],
        outputs: [],
        cycleTicks: 300, // 60 s at 5 Hz
    },
    'burn-zero-point-conduit': {
        // Zero-Point Tap — T5 Energy endgame generator. The lore frames it as
        // self-sustaining; mechanically we consume one zero-point-conduit per
        // ~2-minute cycle for unmatched output. Built from a T5 good means
        // the player has to keep the T5 chain running to keep the lights on.
        id: 'burn-zero-point-conduit',
        name: 'Tap Zero-Point Conduit',
        inputs: [{ resource: 'zero-point-conduit', qty: 1 }],
        outputs: [],
        cycleTicks: 600, // 120 s at 5 Hz
    },
    // ── P15 cross-track endgame recipes ─────────────────────────────────────
    // The "trinity" recipes. Each requires keystones from multiple T5 tracks
    // running concurrently. Long cycles (2 min+) so producing one is a
    // milestone moment, not a routine output. Producing all three is the
    // final gate for Galactic Laureate rank.
    'forge-chronosteel': {
        // Singularity Forge alt. Cross-track keystones (Materials + Energy +
        // Biotech) bound with singularity-dust. The flagship endgame recipe.
        id: 'forge-chronosteel',
        name: 'Forge Chronosteel',
        inputs: [
            { resource: 'resonant-lattice', qty: 1 },
            { resource: 'aeon-cell', qty: 1 },
            { resource: 'transcendent-cortex', qty: 1 },
            { resource: 'singularity-dust', qty: 1 },
        ],
        outputs: [{ resource: 'chronosteel', qty: 1 }],
        cycleTicks: 600, // 120 s at 5 Hz
    },
    'cast-eternium': {
        // Aeon Reactor alt. Quantum-circuit + resonant-coil + neural-substrate
        // + plasma-conduit — a four-input recipe that touches every T4 track
        // (matrix substrate + photonics + biotech + energy).
        id: 'cast-eternium',
        name: 'Cast Eternium',
        inputs: [
            { resource: 'quantum-circuit', qty: 1 },
            { resource: 'resonant-coil', qty: 1 },
            { resource: 'neural-substrate', qty: 1 },
            { resource: 'plasma-conduit', qty: 1 },
        ],
        outputs: [{ resource: 'eternium', qty: 1 }],
        cycleTicks: 540,
    },
    'grow-pyriton': {
        // Transcendent Vat alt. Two zero-point-conduits + a gene-archive +
        // void-glass substrate. Heaviest endgame energy-track demand.
        id: 'grow-pyriton',
        name: 'Grow Pyriton',
        inputs: [
            { resource: 'zero-point-conduit', qty: 2 },
            { resource: 'gene-archive', qty: 1 },
            { resource: 'void-glass', qty: 1 },
        ],
        outputs: [{ resource: 'pyriton', qty: 1 }],
        cycleTicks: 540,
    },
};
export const MACHINE_SPECS = {
    // ── Pre-P6 starter set ──────────────────────────────────────────────────
    'mining-drill': {
        id: 'mining-drill',
        name: 'Mining Drill',
        kind: 'extractor',
        label: 'M',
        bufferCapacity: 50,
        // The drill can extract from any of the standard T1 deposits — placement
        // chooses the matching recipe automatically based on the tile's `resource`
        // field. Bauxite & copper join iron + coal as of P6.
        recipes: [
            'extract-iron-ore',
            'extract-coal',
            'extract-copper-ore',
            'extract-bauxite',
        ],
        cost: 60,
        idleDrawKw: 1,
        activeDrawKw: 3,
    },
    smelter: {
        id: 'smelter',
        name: 'Smelter',
        kind: 'refiner',
        label: 'S',
        bufferCapacity: 50,
        recipes: ['smelt-iron-ingot', 'refine-fuel'],
        cost: 100,
        idleDrawKw: 1,
        activeDrawKw: 4,
    },
    'storage-silo': {
        id: 'storage-silo',
        name: 'Storage Silo',
        kind: 'storage',
        label: 'V',
        bufferCapacity: 5000,
        recipes: [],
        cost: 40,
        idleDrawKw: 0.5,
        // Solids only — the canonical default. Fluid/gas variants below.
        acceptedPhases: ['solid'],
    },
    'trade-port': {
        id: 'trade-port',
        name: 'Trade Port',
        kind: 'market-sink',
        label: '$',
        bufferCapacity: 5000,
        recipes: [],
        cost: 120,
        idleDrawKw: 0.5,
        // Sells anything — tankers and runners can both unload here.
        acceptedPhases: ['solid', 'fluid', 'gas'],
    },
    // ── P5 generator & storage specs ────────────────────────────────────────
    'solar-collector': {
        id: 'solar-collector',
        name: 'Solar Collector',
        kind: 'generator',
        label: '☼',
        bufferCapacity: 0,
        recipes: [],
        cost: 80,
        generationKw: 5,
    },
    'combustion-cell': {
        id: 'combustion-cell',
        name: 'Combustion Cell',
        kind: 'generator',
        label: '⚛',
        bufferCapacity: 20,
        recipes: ['burn-refined-fuel'],
        cost: 200,
        generationKw: 15,
        idleDrawKw: 0.2,
        // Combustion cells need fuel piped in, which is a fluid (refined-fuel).
        acceptedPhases: ['fluid'],
    },
    'capacitor-array': {
        id: 'capacitor-array',
        name: 'Capacitor Array',
        kind: 'capacitor',
        label: '⊜',
        bufferCapacity: 0,
        recipes: [],
        cost: 150,
        capacitorKwh: 50,
        idleDrawKw: 0.1,
    },
    // ── P6 extractors ────────────────────────────────────────────────────────
    'heavy-mining-drill': {
        id: 'heavy-mining-drill',
        name: 'Heavy Mining Drill',
        kind: 'extractor',
        label: 'H',
        bufferCapacity: 100,
        recipes: [
            'heavy-extract-iron-ore',
            'heavy-extract-coal',
            'heavy-extract-copper-ore',
            'heavy-extract-bauxite',
        ],
        cost: 220,
        idleDrawKw: 2,
        activeDrawKw: 6,
    },
    'atmospheric-collector': {
        id: 'atmospheric-collector',
        name: 'Atmospheric Collector',
        kind: 'extractor',
        label: 'A',
        bufferCapacity: 80,
        // Pulls feedstock out of the air — no deposit required. World.addMachine
        // recognises this spec as deposit-free (kind === extractor && recipes do
        // not require a tile match).
        recipes: ['collect-water', 'collect-oxygen'],
        cost: 180,
        idleDrawKw: 1,
        activeDrawKw: 3,
    },
    // ── P6 refiners ──────────────────────────────────────────────────────────
    'alloy-smelter': {
        // Copper-track counterpart to the Smelter. Higher draw, higher buffer —
        // the moment the player has copper-ore, this is the natural next placement.
        id: 'alloy-smelter',
        name: 'Alloy Smelter',
        kind: 'refiner',
        label: 'A',
        bufferCapacity: 60,
        recipes: ['smelt-copper-plate'],
        cost: 140,
        idleDrawKw: 1.5,
        activeDrawKw: 5,
    },
    kiln: {
        id: 'kiln',
        name: 'Kiln',
        kind: 'refiner',
        label: 'K',
        bufferCapacity: 60,
        recipes: ['kiln-alumina'],
        cost: 140,
        idleDrawKw: 1,
        activeDrawKw: 4,
    },
    'chemical-plant': {
        // Aspirational placeholder for the lore's full chemistry track. Currently
        // runs the same hydrolysis recipe as the Hydrolysis Cell so it's useful
        // out of the gate; future phases bind it to chains the Hydrolysis Cell
        // can't (acid baths, biochemistry).
        id: 'chemical-plant',
        name: 'Chemical Plant',
        kind: 'refiner',
        label: 'P',
        bufferCapacity: 40,
        recipes: ['electrolyse-water'],
        cost: 220,
        idleDrawKw: 2,
        activeDrawKw: 7,
        acceptedPhases: ['fluid'],
    },
    'hydrolysis-cell': {
        id: 'hydrolysis-cell',
        name: 'Hydrolysis Cell',
        kind: 'refiner',
        label: 'Y',
        bufferCapacity: 30,
        recipes: ['electrolyse-water'],
        cost: 200,
        idleDrawKw: 1,
        activeDrawKw: 6,
        acceptedPhases: ['fluid'],
    },
    electrolyzer: {
        // Higher-throughput cousin of the Hydrolysis Cell — same recipe, longer
        // cycle but bigger buffer means it pairs better with one Atmospheric
        // Collector running flat-out.
        id: 'electrolyzer',
        name: 'Electrolyzer',
        kind: 'refiner',
        label: 'E',
        bufferCapacity: 60,
        recipes: ['electrolyse-water'],
        cost: 260,
        idleDrawKw: 1.5,
        activeDrawKw: 8,
        acceptedPhases: ['fluid'],
    },
    'cryo-separator': {
        id: 'cryo-separator',
        name: 'Cryo Separator',
        kind: 'refiner',
        label: 'X',
        bufferCapacity: 30,
        recipes: ['compress-hydrogen-cryo'],
        cost: 320,
        idleDrawKw: 2.5,
        activeDrawKw: 10,
        acceptedPhases: ['gas'],
    },
    // ── P6 fabricators ──────────────────────────────────────────────────────
    'forming-press': {
        // Reserved for shaped-metal fabrication chains. P6 ships the spec but no
        // recipes yet; future phases (T3) will give it bearings, plates, etc.
        id: 'forming-press',
        name: 'Forming Press',
        kind: 'refiner',
        label: 'F',
        bufferCapacity: 40,
        recipes: [],
        cost: 200,
        idleDrawKw: 1,
        activeDrawKw: 4,
    },
    'wire-extruder': {
        id: 'wire-extruder',
        name: 'Wire Extruder',
        kind: 'refiner',
        label: 'W',
        bufferCapacity: 40,
        recipes: ['extrude-copper-wire'],
        cost: 160,
        idleDrawKw: 1,
        activeDrawKw: 3,
    },
    assembler: {
        id: 'assembler',
        name: 'Assembler',
        kind: 'refiner',
        label: 'B',
        bufferCapacity: 50,
        recipes: ['assemble-basic-circuit'],
        cost: 240,
        idleDrawKw: 1.5,
        activeDrawKw: 6,
    },
    // ── P6 phase-tagged storage ─────────────────────────────────────────────
    'fluid-tank': {
        id: 'fluid-tank',
        name: 'Fluid Tank',
        kind: 'storage',
        label: 'L',
        bufferCapacity: 4000,
        recipes: [],
        cost: 80,
        idleDrawKw: 0.5,
        acceptedPhases: ['fluid'],
        // Uninsulated — accepts fluids but NOT cryogenic ones (they'd flash off).
        acceptsCryogenic: false,
    },
    'pressure-vessel': {
        id: 'pressure-vessel',
        name: 'Pressure Vessel',
        kind: 'storage',
        label: 'G',
        bufferCapacity: 3000,
        recipes: [],
        cost: 120,
        idleDrawKw: 0.7,
        acceptedPhases: ['gas'],
    },
    'cryo-tank': {
        id: 'cryo-tank',
        name: 'Cryo Tank',
        kind: 'storage',
        label: 'Y',
        bufferCapacity: 2500,
        recipes: [],
        cost: 200,
        // Cryo storage runs a constant chiller — meaningful idle draw.
        idleDrawKw: 1.5,
        acceptedPhases: ['fluid'],
        acceptsCryogenic: true,
    },
    // ── P6A biotech machines ─────────────────────────────────────────────────
    'algae-reactor': {
        // Extractor that needs a `water-source` tile (not a deposit). Slow
        // baseline cycle — the lore says algae growth is light-limited; in
        // mechanical terms it's the cheapest extractor to place but the slowest
        // to fill its buffer, so players are nudged toward more reactors / a
        // booster nearby rather than upgrading.
        id: 'algae-reactor',
        name: 'Algae Reactor',
        kind: 'extractor',
        label: 'L',
        bufferCapacity: 60,
        recipes: ['extract-algae-slurry'],
        cost: 110,
        idleDrawKw: 0.8,
        activeDrawKw: 2,
    },
    fermentor: {
        id: 'fermentor',
        name: 'Fermentor',
        kind: 'refiner',
        label: 'F',
        bufferCapacity: 60,
        // Two recipes: cultures (single fluid input) and biofuel (mixed inputs).
        // Player flips between them via the existing recipe switcher.
        recipes: ['culture-microbes', 'press-biomass', 'ferment-biofuel'],
        cost: 180,
        idleDrawKw: 1,
        activeDrawKw: 5,
        // Accepts both phases — culture is fluid, bio-mass is solid.
        acceptedPhases: ['solid', 'fluid'],
    },
    'culture-lab': {
        id: 'culture-lab',
        name: 'Culture Lab',
        kind: 'refiner',
        label: 'C',
        bufferCapacity: 40,
        recipes: ['brew-pharma-precursor'],
        cost: 320,
        // Heaviest power draw outside the cryo separator — pharma is expensive.
        idleDrawKw: 2.5,
        activeDrawKw: 9,
        acceptedPhases: ['solid', 'fluid'],
    },
    // ── P7B T3 specs (research-gated) ────────────────────────────────────────
    'plasma-refinery': {
        id: 'plasma-refinery',
        name: 'Plasma Refinery',
        kind: 'refiner',
        label: 'R',
        // Big buffer for the heavy recipes (3 inputs, 100+ tick cycles).
        bufferCapacity: 80,
        recipes: ['forge-superalloy', 'fire-ceramic-composite', 'forge-plasma-cell'],
        cost: 800,
        idleDrawKw: 4,
        activeDrawKw: 18,
        acceptedPhases: ['solid', 'fluid', 'gas'],
        requiresResearch: 'unlock-t3-materials',
    },
    'wafer-mill': {
        id: 'wafer-mill',
        name: 'Wafer Mill',
        kind: 'refiner',
        label: 'M',
        bufferCapacity: 60,
        recipes: ['mill-semiconductor-wafer'],
        cost: 600,
        idleDrawKw: 3,
        activeDrawKw: 12,
        requiresResearch: 'unlock-t3-materials',
    },
    'machining-center': {
        id: 'machining-center',
        name: 'Machining Center',
        kind: 'refiner',
        label: 'N',
        bufferCapacity: 60,
        recipes: ['machine-precision-bearing'],
        cost: 720,
        idleDrawKw: 3,
        activeDrawKw: 14,
        requiresResearch: 'unlock-t3-materials',
    },
    'nuclear-cell-plant': {
        id: 'nuclear-cell-plant',
        name: 'Nuclear Cell Plant',
        kind: 'refiner',
        label: 'U',
        bufferCapacity: 80,
        recipes: ['assemble-lithium-ion', 'synthesize-fusion-precursor'],
        cost: 1100,
        idleDrawKw: 5,
        activeDrawKw: 22,
        acceptedPhases: ['solid', 'fluid'],
        requiresResearch: 'unlock-t3-energy',
    },
    'bio-reactor-array': {
        id: 'bio-reactor-array',
        name: 'Bio-Reactor Array',
        kind: 'refiner',
        label: 'O',
        bufferCapacity: 70,
        recipes: ['culture-antibiotic', 'synth-protein-batch', 'grow-organ-scaffold'],
        cost: 900,
        idleDrawKw: 4,
        activeDrawKw: 16,
        acceptedPhases: ['solid', 'fluid'],
        requiresResearch: 'unlock-t3-biotech',
    },
    // ── P8+ T4 specs (research-gated) ────────────────────────────────────────
    'matrix-core': {
        // Keystone T4 spec. Currently runs the mesh-composite recipe — the
        // gateway substrate that feeds every other T4 chain. Future Cohesion
        // Matrix mechanic phase will rewire this into a station-wide
        // networked-storage hub; ships now as a heavyweight refiner.
        id: 'matrix-core',
        name: 'Matrix Core',
        kind: 'refiner',
        label: 'X',
        bufferCapacity: 100,
        recipes: ['weave-mesh-composite'],
        cost: 1800,
        idleDrawKw: 8,
        activeDrawKw: 30,
        acceptedPhases: ['solid', 'fluid', 'gas'],
        requiresResearch: 'unlock-t4-matrix',
    },
    'crystal-lathe': {
        id: 'crystal-lathe',
        name: 'Crystal Lathe',
        kind: 'refiner',
        label: 'L',
        bufferCapacity: 80,
        recipes: ['grow-quantum-crystal', 'forge-exotic-alloy'],
        cost: 1400,
        idleDrawKw: 6,
        activeDrawKw: 24,
        acceptedPhases: ['solid', 'fluid', 'gas'],
        requiresResearch: 'unlock-t4-matrix',
    },
    'disk-fab': {
        id: 'disk-fab',
        name: 'Disk Fab',
        kind: 'refiner',
        label: 'D',
        bufferCapacity: 60,
        recipes: ['fab-matrix-disk'],
        cost: 1200,
        idleDrawKw: 5,
        activeDrawKw: 18,
        requiresResearch: 'unlock-t4-matrix',
    },
    'photonic-loom': {
        id: 'photonic-loom',
        name: 'Photonic Loom',
        kind: 'refiner',
        label: 'P',
        bufferCapacity: 80,
        recipes: ['spin-photonic-cell', 'lay-plasma-conduit'],
        cost: 1500,
        idleDrawKw: 6,
        activeDrawKw: 22,
        requiresResearch: 'unlock-t4-photonics',
    },
    'coil-forge': {
        id: 'coil-forge',
        name: 'Coil Forge',
        kind: 'refiner',
        label: 'Q',
        bufferCapacity: 80,
        recipes: ['wind-resonant-coil', 'imprint-quantum-circuit'],
        cost: 1700,
        idleDrawKw: 7,
        activeDrawKw: 26,
        requiresResearch: 'unlock-t4-photonics',
    },
    'neural-vat': {
        id: 'neural-vat',
        name: 'Neural Vat',
        kind: 'refiner',
        label: 'V',
        bufferCapacity: 80,
        recipes: ['vat-neural-substrate', 'grow-bio-neural-cortex'],
        cost: 1600,
        idleDrawKw: 6,
        activeDrawKw: 22,
        acceptedPhases: ['solid', 'fluid'],
        requiresResearch: 'unlock-t4-organics',
    },
    // ── P8+ T5 specs (research-gated) ────────────────────────────────────────
    // Endgame fabricators. Costs are deliberately steep — a single T5 spec
    // commits the player to a sustained T4 supply chain. Power draws are the
    // highest in the game; balancing brownouts at this tier is part of the
    // intended difficulty curve.
    'singularity-forge': {
        id: 'singularity-forge',
        name: 'Singularity Forge',
        kind: 'refiner',
        label: 'Σ',
        bufferCapacity: 100,
        // P15 — also runs forge-chronosteel, the cross-track Laureate recipe.
        recipes: ['forge-singularity-dust', 'forge-chronosteel'],
        cost: 4500,
        idleDrawKw: 14,
        activeDrawKw: 50,
        acceptedPhases: ['solid', 'fluid', 'gas'],
        requiresResearch: 'unlock-t5-singularity',
    },
    'lattice-weaver': {
        id: 'lattice-weaver',
        name: 'Lattice Weaver',
        kind: 'refiner',
        label: 'Λ',
        bufferCapacity: 100,
        recipes: ['weave-resonant-lattice'],
        cost: 4200,
        idleDrawKw: 12,
        activeDrawKw: 42,
        requiresResearch: 'unlock-t5-singularity',
    },
    'aeon-reactor': {
        id: 'aeon-reactor',
        name: 'Aeon Reactor',
        kind: 'refiner',
        label: 'Æ',
        bufferCapacity: 120,
        // P15 — also runs cast-eternium, the cross-track Laureate recipe.
        recipes: ['power-aeon-cell', 'lay-zero-point-conduit', 'cast-eternium'],
        cost: 5000,
        idleDrawKw: 16,
        activeDrawKw: 60,
        acceptedPhases: ['solid', 'fluid', 'gas'],
        requiresResearch: 'unlock-t5-aeon',
    },
    'void-fabricator': {
        id: 'void-fabricator',
        name: 'Void Fabricator',
        kind: 'refiner',
        label: 'Ø',
        bufferCapacity: 90,
        recipes: ['cast-void-glass'],
        cost: 3800,
        idleDrawKw: 10,
        activeDrawKw: 36,
        acceptedPhases: ['solid', 'gas'],
        requiresResearch: 'unlock-t5-aeon',
    },
    'transcendent-vat': {
        id: 'transcendent-vat',
        name: 'Transcendent Vat',
        kind: 'refiner',
        label: 'Ψ',
        bufferCapacity: 100,
        // P15 — also runs grow-pyriton, the cross-track Laureate recipe.
        recipes: ['transcend-cortex', 'archive-genes', 'grow-pyriton'],
        cost: 4800,
        idleDrawKw: 13,
        activeDrawKw: 46,
        acceptedPhases: ['solid', 'fluid'],
        requiresResearch: 'unlock-t5-transcendent',
    },
    // ── P9 Cohesion Matrix — virtual inventory tap ──────────────────────────
    // A matrix-port has no local buffer at all. Drones treat it as an unlimited
    // multi-resource source/sink that proxies to `Station.matrixNetwork.contents`.
    // Network capacity comes from placed matrix-cores (+2000 each) and disk-fabs
    // (+1500 each), so the player has to ALSO be running the T4 production chain
    // to grow the network — otherwise capacity stays at zero and ports refuse
    // deposits. `bufferCapacity` is set to 0 to make the lack-of-local-buffer
    // explicit (any inadvertent direct-deposit would be rejected immediately).
    //
    // Accepts every phase + cryogenic so the network can be a single one-stop
    // hub for the entire station's logistics — that's the whole pitch.
    'matrix-port': {
        id: 'matrix-port',
        name: 'Matrix Port',
        kind: 'matrix-port',
        label: '◇',
        bufferCapacity: 0,
        recipes: [],
        cost: 600,
        idleDrawKw: 0.6,
        acceptedPhases: ['solid', 'fluid', 'gas'],
        acceptsCryogenic: true,
        requiresResearch: 'unlock-t4-matrix',
    },
    // ── P14 extended generator family (lore §6) ─────────────────────────────
    // Three new generators that round out the energy track. Each plugs into
    // the existing `kind: 'generator'` pipeline (see `power.ts`) — same
    // generationKw + recipe-driven fuel consumption pattern as the original
    // combustion-cell, just at higher tier values.
    //
    // Power scaling: kW values follow the lore §6 table, scaled down by 4×
    // (lore listed 500/1000/5000 kW; we ship 125/250/1250). The smaller
    // numbers keep the early-game number-feel intact while still making
    // each new generator a clearly upgraded option.
    'bio-burner': {
        // T2 alt to combustion-cell. Burns biotech-track fuel for slightly
        // higher kW than the basic combustion cell, with a bigger draw of
        // ambient power (it's a hotter system).
        id: 'bio-burner',
        name: 'Bio-Burner',
        kind: 'generator',
        label: '⚙',
        bufferCapacity: 25,
        recipes: ['burn-fermented-fuel'],
        cost: 280,
        generationKw: 25,
        idleDrawKw: 0.4,
        acceptedPhases: ['fluid'],
    },
    'fusion-reactor': {
        // T3 Energy — the mid-game "everything works again" generator.
        // 125 kW from one fusion-precursor every 12 s.
        id: 'fusion-reactor',
        name: 'Fusion Reactor',
        kind: 'generator',
        label: 'Φ',
        bufferCapacity: 30,
        recipes: ['burn-fusion-precursor'],
        cost: 1500,
        generationKw: 125,
        idleDrawKw: 1.5,
        acceptedPhases: ['fluid'],
        acceptsCryogenic: true, // fusion-precursor is cryogenic
        requiresResearch: 'unlock-t3-energy',
    },
    'heavy-reactor': {
        // T3 Energy alt (lore's "Thorium Reactor" slot). Highest sustained
        // output of any T3 generator but a 60-second cycle creates demand-
        // smoothing problems — a Capacitor Array is essentially required to
        // bridge the gap between fuel pulses.
        id: 'heavy-reactor',
        name: 'Heavy Reactor',
        kind: 'generator',
        label: 'Ω',
        bufferCapacity: 20,
        recipes: ['burn-plasma-cell'],
        cost: 2400,
        generationKw: 250,
        idleDrawKw: 3,
        requiresResearch: 'unlock-t3-energy',
    },
    'zero-point-tap': {
        // T5 Energy — the lore's endgame generator. Eclipses every other power
        // source if the player can keep zero-point-conduit production flowing.
        // Gated behind unlock-t5-aeon (the same node that unlocks aeon-reactor,
        // which is what produces zero-point-conduit) so the supply chain is
        // available the moment the spec is.
        id: 'zero-point-tap',
        name: 'Zero-Point Tap',
        kind: 'generator',
        label: '✶',
        bufferCapacity: 15,
        recipes: ['burn-zero-point-conduit'],
        cost: 6500,
        generationKw: 1250,
        idleDrawKw: 8,
        requiresResearch: 'unlock-t5-aeon',
    },
    // ── P19A — Power-pole tiers ──────────────────────────────────────────
    // Distribute kW spatially: a machine receives power only if it's within
    // some pole's `coverageRadius` AND that pole is transitively connected
    // (via pole-to-pole `linkRadius` chains) to a generator that's also
    // covered. Isolated machines are dark even if total station kW is plenty.
    'power-pole-t1': {
        id: 'power-pole-t1',
        name: 'Power Pole · T1',
        kind: 'pole',
        label: '╪',
        bufferCapacity: 0,
        recipes: [],
        cost: 40,
        coverageRadius: 2, // 5×5 covered tiles
        linkRadius: 5,
    },
    'power-pole-t2': {
        id: 'power-pole-t2',
        name: 'Power Pole · T2',
        kind: 'pole',
        label: '╫',
        bufferCapacity: 0,
        recipes: [],
        cost: 180,
        coverageRadius: 4, // 9×9 covered tiles
        linkRadius: 9,
        idleDrawKw: 0.1,
        requiresResearch: 'unlock-power-grid-t2',
    },
    'power-pole-t3': {
        id: 'power-pole-t3',
        name: 'Power Pole · T3',
        kind: 'pole',
        label: '╬',
        bufferCapacity: 0,
        recipes: [],
        cost: 600,
        coverageRadius: 6, // 13×13 covered tiles
        linkRadius: 14,
        idleDrawKw: 0.3,
        requiresResearch: 'unlock-power-grid-t3',
    },
    'battery-pole': {
        id: 'battery-pole',
        name: 'Battery Pole',
        kind: 'pole',
        label: '⊠',
        bufferCapacity: 0,
        recipes: [],
        cost: 300,
        coverageRadius: 2, // same as t1 — its job is buffering, not coverage
        linkRadius: 5,
        capacitorKwh: 100,
    },
    // ── P17C — Cross-Station Port ──────────────────────────────────────
    // Charter-wide shared stockpile tap. Each CSP placed on any station
    // contributes to a single pool that drones can source/sink against
    // from any station. Pattern mirrors `matrix-port` (Cohesion Matrix)
    // but at the charter scope instead of the station scope.
    //
    // Research-gated behind `unlock-cross-station-logistics` so a fresh
    // charter can't immediately bypass per-station logistics; the player
    // earns the unlock as part of expanding into Hub Bravo + beyond.
    'cross-station-port': {
        id: 'cross-station-port',
        name: 'Cross-Station Port',
        kind: 'cross-station',
        label: '⌬',
        bufferCapacity: 0,
        recipes: [],
        cost: 500,
        idleDrawKw: 0.5,
        acceptedPhases: ['solid', 'fluid', 'gas'],
        requiresResearch: 'unlock-cross-station-logistics',
    },
};
export function getRecipe(id) {
    const r = RECIPES[id];
    if (!r)
        throw new Error(`Unknown recipe: ${id}`);
    return r;
}
export function getMachineSpec(id) {
    const s = MACHINE_SPECS[id];
    if (!s)
        throw new Error(`Unknown machine spec: ${id}`);
    return s;
}
/**
 * Returns the phases this machine's INPUT buffer accepts. Defaults to
 * `['solid']` for any spec that didn't explicitly opt in — which keeps every
 * pre-P6 machine working without a code change.
 */
export function getMachineAcceptedPhases(id) {
    return MACHINE_SPECS[id].acceptedPhases ?? ['solid'];
}
/** True iff this machine accepts cryogenic fluids in its input buffer. */
export function machineAcceptsCryogenic(id) {
    return MACHINE_SPECS[id].acceptsCryogenic === true;
}
/**
 * P18 v1.1 — Recipe-mismatch detection.
 *
 * Returns `true` iff the machine has at least one resource in its input
 * buffer that the currently-selected recipe doesn't actually consume.
 *
 * The classic stall: the player belts coal into a smelter that's still set
 * to `smelt-iron-ingot`. The smelter's `acceptedPhases` includes solids,
 * so the input buffer accepts coal happily — but the recipe never advances
 * because no iron-ore is arriving. Coal piles up, the line stalls, the
 * player thinks the game is broken.
 *
 * The renderer uses this to draw a ⚠ stamp on the offending machine; the
 * inspector uses it to call out which resource is mismatched.
 *
 * Recipeless machines (storage silos, trade ports, solar collectors,
 * capacitor arrays — anything with `recipeId === null`) are never
 * mismatched: they don't gate on resource identity at all.
 */
export function hasMismatchedInput(machine) {
    if (machine.recipeId === null)
        return false;
    const recipe = RECIPES[machine.recipeId];
    if (!recipe)
        return false;
    const validInputs = new Set(recipe.inputs.map((io) => io.resource));
    for (const id of Object.keys(machine.input)) {
        const qty = machine.input[id] ?? 0;
        if (qty > 0 && !validInputs.has(id))
            return true;
    }
    return false;
}
/**
 * P18 v1.1 — Returns the set of mismatched resource ids (those present in
 * `machine.input` but not consumed by the current recipe). Used by the
 * inspector popover to name the specific resource(s) blocking progress.
 */
export function mismatchedInputResources(machine) {
    if (machine.recipeId === null)
        return [];
    const recipe = RECIPES[machine.recipeId];
    if (!recipe)
        return [];
    const validInputs = new Set(recipe.inputs.map((io) => io.resource));
    const out = [];
    for (const id of Object.keys(machine.input)) {
        const qty = machine.input[id] ?? 0;
        if (qty > 0 && !validInputs.has(id))
            out.push(id);
    }
    return out;
}
//# sourceMappingURL=machines.js.map